import React, { useEffect } from 'react';
import { Form, InputNumber, Select, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined } from '@ant-design/icons';
import { type FormInstance } from 'antd/es/form';
import { type FormListFieldData } from 'antd/es/form/FormList';
import AnimatedDiv from '../../../../../common/components/AnimatedDiv/AnimatedDiv';
import { currencyAmountValidator } from '@/common/components/MultipleOfValidator';

const { Option } = Select;

// --- Age Premium Row Component ---
interface AgePremiumRowProps {
    premiumKey: React.Key;
    premiumName: number;
    premiumRestField: Omit<FormListFieldData, 'key' | 'name'>;
    categoryName: number;
    removePremium: (index: number) => void;
    form: FormInstance;
    genderOptions: { label: string; value: number }[];
    maritalStatusOptions: { label: string; value: number }[];
    currencyISOCode: string | undefined;
}

const AgePremiumRow: React.FC<AgePremiumRowProps> = ({
    premiumKey,
    premiumName,
    premiumRestField,
    categoryName,
    removePremium,
    form,
    genderOptions,
    maritalStatusOptions,
    currencyISOCode
}) => {
    const { t } = useTranslation();
    const ageRangeCondition = Form.useWatch(['planCategories', categoryName, 'ageRangeCondition'], form);
    const agePremiums = Form.useWatch(['planCategories', categoryName, 'agePremiums'], form);
    const showGender = ageRangeCondition === 2 || ageRangeCondition === 4;
    const showMaritalStatus = ageRangeCondition === 3 || ageRangeCondition === 4;
    const rowGridTemplateColumns = [
        '32px',
        'minmax(120px, 1fr)',
        'minmax(120px, 1fr)',
        ...(showGender ? ['minmax(140px, 1fr)'] : []),
        ...(showMaritalStatus ? ['minmax(160px, 1fr)'] : []),
        'minmax(140px, 1fr)',
        '32px',
    ].join(' ');
   
    useEffect(() => {
        const ageFromPath = ['planCategories', categoryName, 'agePremiums', premiumName, 'ageFrom'];
        const ageToPath = ['planCategories', categoryName, 'agePremiums', premiumName, 'ageTo'];

        if (form.isFieldTouched(ageFromPath) || form.isFieldTouched(ageToPath)) {
            form.validateFields([ageFromPath, ageToPath]);
        }
    }, [JSON.stringify(agePremiums), form, categoryName, premiumName]);
// useEffect(() => {
        
        

//         // Clear gender if it's not supposed to be shown
//         if (!(ageRangeCondition === 2 || ageRangeCondition === 4)) {
//             form.setFieldsValue({
//                 planCategories: {
//                     [categoryName]: {
//                         agePremiums: {
//                             [premiumName]: {
//                                 gender: undefined,
//                             },
//                         },
//                     },
//                 },
//             });
//         }

//         // Clear marital status if it's not supposed to be shown
//         if (!(ageRangeCondition === 3 || ageRangeCondition === 4)) {
//             form.setFieldsValue({
//                 planCategories: {
//                     [categoryName]: {
//                         agePremiums: {
//                             [premiumName]: {
//                                 maritalStatus: undefined,
//                             },
//                         },
//                     },
//                 },
//             });
//         }
//     }, [ageRangeCondition, form, categoryName, premiumName]);


    return (
        <AnimatedDiv>
            <div
                key={premiumKey}
                style={{
                    display: 'grid',
                    gridTemplateColumns: rowGridTemplateColumns,
                    gap: 12,
                    alignItems: 'start',
                    padding: '10px 12px',
                    background: premiumName % 2 === 0 ? '#fafbfd' : '#ffffff',
                    borderBottom: '1px solid #f0f2f5',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f6f8fb';
                    e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #e1e8f0';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = premiumName % 2 === 0 ? '#fafbfd' : '#ffffff';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            >
                <div
                    style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: 'linear-gradient(135deg, #0f85ae 0%, #61aeb2 100%)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        marginTop: 8,
                    }}
                >
                    {premiumName + 1}
                </div>

                <Form.Item
                    {...premiumRestField}
                    name={[premiumName, 'ageFrom']}
                    style={{ marginBottom: 0 }}
                    rules={[
                        { required: true, message: t('Missing age from') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const agePremiums = getFieldValue(['planCategories', categoryName, 'agePremiums']);
                                const ageTo = getFieldValue(['planCategories', categoryName, 'agePremiums', premiumName, 'ageTo']);
                                const currentGender = getFieldValue(['planCategories', categoryName, 'agePremiums', premiumName, 'gender']);
                                const currentMaritalStatus = getFieldValue(['planCategories', categoryName, 'agePremiums', premiumName, 'maritalStatus']);
                                if (value === undefined || ageTo === undefined) {
                                    return Promise.resolve();
                                }
                                for (let i = 0; i < agePremiums.length; i++) {
                                    if (i === premiumName) continue;
                                    const otherRange = agePremiums[i];
                                    if (otherRange && otherRange.ageFrom !== undefined && otherRange.ageTo !== undefined) {
                                        let shouldCheckOverlap = true;
                                        if (ageRangeCondition === 2 || ageRangeCondition === 4) {
                                            if (currentGender !== otherRange.gender) {
                                                shouldCheckOverlap = false;
                                            }
                                        }
                                        if (shouldCheckOverlap && (ageRangeCondition === 3 || ageRangeCondition === 4)) {
                                            if (currentMaritalStatus !== otherRange.maritalStatus) {
                                                shouldCheckOverlap = false;
                                            }
                                        }
                                        if (shouldCheckOverlap) {
                                            if (Math.max(value, otherRange.ageFrom) <= Math.min(ageTo, otherRange.ageTo)) {
                                                return Promise.reject(new Error(t('Age ranges cannot overlap.')));
                                            }
                                        }
                                    }
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <InputNumber min={0} max={120} step={1} precision={0} style={{ width: '100%' }} size="large" placeholder={t('Age From')} />
                </Form.Item>

                <Form.Item
                    {...premiumRestField}
                    name={[premiumName, 'ageTo']}
                    style={{ marginBottom: 0 }}
                    rules={[
                        { required: true, message: t('Missing age to') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const ageFrom = getFieldValue(['planCategories', categoryName, 'agePremiums', premiumName, 'ageFrom']);
                                const currentGender = getFieldValue(['planCategories', categoryName, 'agePremiums', premiumName, 'gender']);
                                const currentMaritalStatus = getFieldValue(['planCategories', categoryName, 'agePremiums', premiumName, 'maritalStatus']);
                                if (ageFrom !== undefined && value < ageFrom) {
                                    return Promise.reject(new Error(t("'Age To' must be greater than or equal to 'Age From'")));
                                }
                                if (value === undefined || ageFrom === undefined) {
                                    return Promise.resolve();
                                }
                                for (let i = 0; i < agePremiums.length; i++) {
                                    if (i === premiumName) continue;
                                    const otherRange = agePremiums[i];
                                    if (otherRange && otherRange.ageFrom !== undefined && otherRange.ageTo !== undefined) {
                                        let shouldCheckOverlap = true;
                                        if (ageRangeCondition === 2 || ageRangeCondition === 4) {
                                            if (currentGender !== otherRange.gender) {
                                                shouldCheckOverlap = false;
                                            }
                                        }
                                        if (shouldCheckOverlap && (ageRangeCondition === 3 || ageRangeCondition === 4)) {
                                            if (currentMaritalStatus !== otherRange.maritalStatus) {
                                                shouldCheckOverlap = false;
                                            }
                                        }
                                        if (shouldCheckOverlap) {
                                            if (Math.max(ageFrom, otherRange.ageFrom) <= Math.min(value, otherRange.ageTo)) {
                                                return Promise.reject(new Error(t('Age ranges cannot overlap.')));
                                            }
                                        }
                                    }
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <InputNumber min={0} max={120} step={1} precision={0} style={{ width: '100%' }} size="large" placeholder={t('Age To')} />
                </Form.Item>

                {showGender ? (
                    <Form.Item
                        {...premiumRestField}
                        name={[premiumName, 'gender']}
                        style={{ marginBottom: 0 }}
                        rules={[{
                            required: ageRangeCondition === 2 || ageRangeCondition === 4, message: t('Please select gender!') }]}
                    >
                        <Select
                            placeholder={t('Select gender')}
                            size="large"
                            showSearch={true}
                            filterOption={(input, option) =>
                                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {genderOptions.map(option => (
                                <Option key={option.value} value={option.value} label={option.label}>{option.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                ) : null}

                {showMaritalStatus ? (
                    <Form.Item
                        {...premiumRestField}
                        name={[premiumName, 'maritalStatus']}
                        style={{ marginBottom: 0 }}
                        rules={[{
                            required: ageRangeCondition === 3 || ageRangeCondition === 4, message: t('Please select marital status!') }]}
                    >
                        <Select
                            placeholder={t('Select marital status')}
                            size="large"
                            showSearch={true}
                            filterOption={(input, option) =>
                                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {maritalStatusOptions.map(option => (
                                <Option key={option.value} value={option.value} label={option.label}>{option.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                ) : null}

                <Form.Item
                    {...premiumRestField}
                    name={[premiumName, 'premiumAmount']}
                    style={{ marginBottom: 0 }}
                    validateFirst={true}
                    rules={[
                        { required: true, message: t('Missing premium amount') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const currentCoverageAmount = getFieldValue(['planCategories', categoryName, 'coverageAmount']);
                                if (value !== undefined && currentCoverageAmount !== undefined && value > currentCoverageAmount) {
                                    return Promise.reject(new Error(t('Premium Amount cannot be greater than Coverage Amount.')));
                                }
                                return Promise.resolve();
                            },
                        }),
                        currencyAmountValidator(currencyISOCode, "")
                    ]}
                >
                    <InputNumber min={1} step={1} precision={0} style={{ width: '100%' }} size="large" placeholder={t('Premium Amount')} />
                </Form.Item>

                {premiumName !== 0 ? (
                    <Button
                        danger
                        type="text"
                        size="small"
                        icon={<DeleteOutlined style={{ color: '#dc2626' }} />}
                        onClick={() => removePremium(premiumName)}
                        title={t('Delete')}
                        style={{ marginTop: 4, opacity: 0.6, transition: 'opacity 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
                    />
                ) : (
                    <div style={{ width: 24 }} />
                )}
            </div>
        </AnimatedDiv>
    );
};

export default AgePremiumRow;